import type { Inline, LegalContent } from "./legal";

const t = (text: string): Inline => ({ t: "text", text });
const b = (text: string): Inline => ({ t: "b", text });
const em = (): Inline => ({ t: "email", text: "info@quintaratech.com" });

export const defaultGovDataRequestContent: LegalContent = {
  meta: {
    title: "Quintara Government Data Request Policy",
    description: "How Quintara handles government and law enforcement requests for data.",
  },
  hero: { eyebrow: "Legal", heading: "Quintara — Government Data Request Policy", dateLabel: "Oct 13, 2026" },
  body: [
    { kind: "h2", text: "1. Purpose and scope" },
    {
      kind: "p",
      spans: [t('Quintara Advertising Services L.L.C ("Quintara") discloses user personal data to a public authority only when the request is lawful, specific and has been reviewed under this policy.')],
    },
    {
      kind: "ul",
      items: [
        [b("Applies to:"), t(" any request, order, warrant, subpoena or demand from a government body, regulator, law-enforcement or security agency, in any country, for personal data Quintara holds or processes.")],
        [b("Covers:"), t(" Platform Data received from Meta (including WhatsApp Business Platform data), client data, and end-user message data, wherever it is stored (including AWS regions in the UAE and Bahrain).")],
        [b("Binds:"), t(" all Quintara staff, contractors, and data processors acting on Quintara's behalf.")],
        [b("Legal basis:"), t(" applicable law, including UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data, and Quintara's contractual obligations under the Meta Platform Terms and WhatsApp Business terms.")],
      ],
    },
    { kind: "h2", text: "2. Principles" },
    {
      kind: "ol",
      items: [
        [b("No voluntary disclosure."), t(" Quintara does not hand over personal data on informal or verbal requests. Every request must come through formal legal process.")],
        [b("No bulk or backdoor access."), t(" Quintara gives no authority direct, standing or bulk access to its systems, and builds no backdoors.")],
        [b("Legality first."), t(" Every request is reviewed for legal validity before any data is touched.")],
        [b("Minimum necessary."), t(" When disclosure is required, only the data strictly needed to comply is released.")],
        [b("Record everything."), t(" Every request and response is documented and kept.")],
        [b("Transparency where allowed."), t(" Affected clients or users are told unless the law prohibits it.")],
      ],
    },
    { kind: "h2", text: "3. Required legality review" },
    {
      kind: "p",
      spans: [t("Every request goes to the Data Protection Officer (DPO) within 1 business day of receipt. Before any data is accessed, the DPO, with legal counsel, checks:")],
    },
    {
      kind: "ol",
      items: [
        [b("Authority:"), t(" the requesting body exists, has jurisdiction over Quintara and the data, and the request is authentic (verified through an official channel, not the sender's own contact details).")],
        [b("Legal basis:"), t(" the request cites a specific law and follows the required process (court order, warrant or statutory demand).")],
        [b("Form:"), t(" it is in writing, signed by an authorised official, and identifies the specific accounts, data types and time period.")],
        [b("Proportionality:"), t(" the scope is necessary and proportionate to the stated purpose.")],
        [b("Conflicts:"), t(" compliance would not breach UAE law, the law of the data subject's country, or Quintara's obligations to Meta and clients.")],
      ],
    },
    {
      kind: "p",
      spans: [t("A request that fails any check is not fulfilled until the defect is cured. Requests from outside the UAE must normally come through a mutual legal assistance treaty or other recognised international process.")],
    },
    { kind: "h2", text: "4. Challenging unlawful requests" },
    {
      kind: "p",
      spans: [t("Quintara challenges any request it considers unlawful, overbroad or unclear, using every reasonable legal avenue available.")],
    },
    {
      kind: "ul",
      items: [
        [b("Clarify or narrow:"), t(" write to the authority asking it to clarify, narrow or withdraw the request.")],
        [b("Formal objection:"), t(" file an objection or appeal with the issuing authority or competent court, where the law allows.")],
        [b("Seek a stay:"), t(" ask for disclosure to be suspended while a challenge is pending, where the law allows.")],
        [b("Escalate:"), t(" the decision to challenge is made by the DPO and a Managing Director, with outside counsel where needed.")],
      ],
    },
    {
      kind: "p",
      spans: [t("Quintara discloses nothing while a challenge is pending, unless a final and binding order requires it.")],
    },
    { kind: "h2", text: "5. Data minimization" },
    {
      kind: "p",
      spans: [t("When disclosure is legally required, Quintara releases the minimum data needed to comply, and nothing more.")],
    },
    {
      kind: "ul",
      items: [
        [t("Only the accounts, data fields and date range named in the request.")],
        [t("Metadata (e.g., account identifiers, timestamps) in preference to message content, where that satisfies the request.")],
        [t("Pseudonymised or redacted data where the request can be met that way.")],
        [t("Data of third parties not named in the request is removed.")],
        [t("Delivered through a secure, encrypted channel to a verified recipient, never by ordinary email.")],
      ],
    },
    {
      kind: "p",
      spans: [t("An engineer pulls the data under DPO supervision, and a second person checks it against the approved scope before it is released.")],
    },
    { kind: "h2", text: "6. Documentation and record-keeping" },
    {
      kind: "p",
      spans: [t("The DPO keeps a confidential Government Request Register and retains each record for at least 5 years.")],
    },
    {
      kind: "table",
      columns: ["Field", "What is recorded"],
      rows: [
        ["Request", "Date received, authority, official's name, country, legal instrument cited"],
        ["Scope", "Accounts, data types and period requested"],
        ["Review", "Legality findings, people involved, legal reasoning"],
        ["Decision", "Fulfilled, narrowed, challenged or refused, and who approved"],
        ["Response", "Exactly what was disclosed, when, by what channel, and to whom"],
        ["Notification", "Whether the client or user was notified, or the legal reason they were not"],
      ],
    },
    {
      kind: "p",
      spans: [t("The register is the source for any transparency reporting and for answers to Meta data protection assessments.")],
    },
    { kind: "h2", text: "7. Notification, emergencies, processors and ownership" },
    {
      kind: "ul",
      items: [
        [b("Client and user notice:"), t(" Quintara notifies the affected business client (and, through it, the end user) before disclosure, unless the law or a court order prohibits it. If notice is delayed, it is given once the restriction ends.")],
        [b("Emergencies:"), t(" a request citing imminent risk to life or of serious physical harm may be expedited. It is still verified, limited to what the emergency needs, documented, and reviewed by the DPO afterwards.")],
        [b("Processors:"), t(" processors (including Amazon Web Services) must forward any government request for Quintara data to Quintara, unless legally barred, and must not disclose on their own. This is written into each processor contract.")],
        [b("Meta:"), t(" where a request concerns Meta Platform Data, Quintara also meets its obligations under the Meta Platform Terms.")],
        [b("Ownership:"), t(" the DPO owns this policy. Requests go to "), em(), t(". Staff who receive a request forward it immediately and take no other action.")],
        [b("Review:"), t(" reviewed every 12 months, and after any significant request or change in law. Approved by the Managing Directors of Quintara Advertising Services L.L.C.")],
      ],
    },
  ],
};
